import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { btnPrimary, type as text, theme } from "../../theme";
import { SheetCard } from "../../components/Drawing";
import { LoadingBlock } from "../../components/Spinner";
import { SeverityPill, input, secondaryBtn, toDateInput, fmtDateOnly } from "../resilience/shared";
import { MS_LABELS, clausesByStandard, msRef, type MsFrameworkId } from "./shared";
import { attempt, useRegister } from "./useRegister";

// The risk-based audit programme (backend lib/auditProgrammePlanning.ts): the next cycle's audits
// proposed from the organisation's own records. Nothing is created until the owner accepts one; an
// accepted audit is an ordinary planned audit, after which its area drops out of the proposal.

interface Reason { kind: string; weight: number; text: string; clauseIds: string[] }
export interface AuditProposal {
  key: string; frameworkId: MsFrameworkId; area: string; title: string; scopeClauses: string[]; score: number;
  priority: "high" | "medium" | "low"; quarter: { index: number; label: string; start: string; end: string };
  plannedDate: string; reasons: Reason[]; scopeText: string; dismissed: boolean;
}
interface ProposalData {
  cycle: { key: string; start: string; end: string; programmeId: string | null; quarters: Array<{ index: number; label: string }> };
  frameworks: MsFrameworkId[];
  proposals: AuditProposal[];
  alreadyPlanned: Array<{ frameworkId: MsFrameworkId; area: string; auditDisplayId: number | null }>;
  dismissals: Array<{ id: string; proposalKey: string; reason: string | null; createdAt: string }>;
}

/** What a proposal fills in on the audit form; the audit route validates it like any other. */
export interface ProposalPrefill {
  programmeId: string; title: string; frameworkIds: string[]; scopeClauses: string[]; scope: string; plannedDate: string;
}

const REASON_LABELS: Record<string, string> = {
  risk: "High risk", finding_open: "Open finding", finding_repeat: "Repeat finding", incident: "Incidents",
  coverage: "Not yet covered", stale_register: "Stale register",
};

export function AuditProposals({ programmes, canAudit, onEdit, onChanged, reloadKey }: {
  programmes: Array<{ id: string; name: string }>;
  /** Changes whenever the page's audits change, so an audit planned from the form drops out here too. */
  reloadKey: number;
  canAudit: boolean;
  onEdit: (prefill: ProposalPrefill) => void;
  onChanged: () => Promise<unknown>;
}) {
  const [programmeId, setProgrammeId] = useState("");
  const { data, loading, error, reload } = useRegister<ProposalData>(`/management-system/audit-proposals${programmeId ? `?programmeId=${encodeURIComponent(programmeId)}` : ""}`);
  const [err, setErr] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  useEffect(() => { if (reloadKey) void reload(); }, [reloadKey, reload]);

  const prefill = (p: AuditProposal): ProposalPrefill => ({
    programmeId: data?.cycle.programmeId ?? "", title: p.title, frameworkIds: [p.frameworkId], scopeClauses: p.scopeClauses,
    scope: p.scopeText, plannedDate: toDateInput(p.plannedDate),
  });
  const accept = async (p: AuditProposal) => {
    const f = prefill(p);
    const body = { ...f, programmeId: f.programmeId || null, plannedDate: p.plannedDate, status: "planned" };
    if (await attempt(() => api.post("/management-system/audits", body), setErr, "Could not plan the audit")) await Promise.all([reload(), onChanged()]);
  };
  const [dismissing, setDismissing] = useState<{ key: string; reason: string } | null>(null);
  const dismiss = async (p: AuditProposal, reason: string) => {
    if (!data) return;
    if (await attempt(() => api.post("/management-system/audit-proposals/dismissals", { cycleKey: data.cycle.key, proposalKey: p.key, reason: reason.trim() || null }), setErr, "Could not set the proposal aside")) {
      setDismissing(null);
      await reload();
    }
  };
  const restore = async (id: string) => {
    if (await attempt(() => api.del(`/management-system/audit-proposals/dismissals/${id}`), setErr, "Could not restore the proposal")) await reload();
  };

  const actions = (p: AuditProposal) => canAudit && (
    <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
      <button type="button" style={btnPrimary} onClick={() => void accept(p)}>Accept</button>
      <button type="button" style={secondaryBtn} onClick={() => onEdit(prefill(p))}>Edit and plan</button>
      <button type="button" style={secondaryBtn} onClick={() => setDismissing({ key: p.key, reason: "" })}>Dismiss</button>
    </span>
  );
  const dismissBox = (p: AuditProposal) => dismissing?.key === p.key && (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
      <input style={{ ...input, flex: "1 1 240px" }} maxLength={2000} autoFocus aria-label="Why it is not needed this cycle"
        placeholder="Why it is not needed this cycle (optional)" value={dismissing.reason}
        onChange={(e) => setDismissing({ key: p.key, reason: e.target.value })} />
      <button type="button" style={secondaryBtn} onClick={() => void dismiss(p, dismissing.reason)}>Set aside</button>
      <button type="button" style={secondaryBtn} onClick={() => setDismissing(null)}>Cancel</button>
    </div>
  );

  const live = data?.proposals.filter((p) => !p.dismissed) ?? [];
  const dismissed = data?.proposals.filter((p) => p.dismissed) ?? [];
  const cycleLabel = data ? `${fmtDateOnly(data.cycle.start)} to ${fmtDateOnly(data.cycle.end)}` : "";

  return (
    <SheetCard label="Proposed audits" reference="9.2.2" actions={
      <select style={{ ...input, width: "auto", padding: "3px 6px" }} value={programmeId} onChange={(e) => setProgrammeId(e.target.value)} aria-label="Cycle">
        <option value="">Next four quarters</option>
        {programmes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    }>
      <p style={{ ...text.hint, margin: "0 0 8px" }}>
        Worked out from your records: high risks and the clauses they link to, open and repeat findings, incidents of the last year, clauses no recent audit has reached, and stale registers. Nothing is planned until you accept it.
      </p>
      {loading ? <LoadingBlock /> : error || !data ? <p style={{ ...text.body, color: theme.color.dangerText }}>{error}</p> : (
        <>
          <div style={{ ...text.hint, marginBottom: 8 }}>
            Cycle {cycleLabel} · {data.frameworks.map((f) => MS_LABELS[f]).join(", ") || "no management system standard"}
            {data.alreadyPlanned.length > 0 && ` · ${data.alreadyPlanned.length} area${data.alreadyPlanned.length === 1 ? "" : "s"} already planned (${[...new Set(data.alreadyPlanned.map((a) => msRef("audit", a.auditDisplayId)))].join(", ")})`}
          </div>
          {err && <p style={{ ...text.body, color: theme.color.dangerText, margin: "0 0 8px" }}>{err}</p>}
          {live.length === 0 ? (
            <p style={{ ...text.body, margin: 0 }}>Nothing in your records points to a further audit this cycle{data.alreadyPlanned.length ? " beyond the ones already planned" : ""}.</p>
          ) : data.cycle.quarters.map((q) => {
            const inQ = live.filter((p) => p.quarter.index === q.index);
            if (!inQ.length) return null;
            // A proposal with a reason beyond coverage gets a card; the areas proposed only because no
            // recent audit reached them are folded into one compact list, so the signals stay on top.
            const signalled = inQ.filter((p) => p.reasons.some((r) => r.kind !== "coverage"));
            const coverageOnly = inQ.filter((p) => !p.reasons.some((r) => r.kind !== "coverage"));
            return (
              <div key={q.index} style={{ marginBottom: 10 }}>
                <div style={{ ...text.label, marginBottom: 4 }}>{q.label}</div>
                <div style={{ display: "grid", gap: 8 }}>
                  {signalled.map((p) => (
                    <div key={p.key} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "8px 10px", background: "var(--c-panel)" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                        <span style={text.itemTitle}>{p.title}</span>
                        <SeverityPill value={p.priority} />
                        <span style={text.hint}>suggested {fmtDateOnly(p.plannedDate)}</span>
                        {actions(p)}
                      </div>
                      {dismissBox(p)}
                      <ul style={{ ...text.body, margin: "6px 0 0", paddingLeft: 18 }}>
                        {p.reasons.map((r, i) => <li key={i}><strong>{REASON_LABELS[r.kind] ?? r.kind}:</strong> {r.text}</li>)}
                      </ul>
                      <div style={{ ...text.hint, marginTop: 4 }}>Clauses: {clausesByStandard(p.scopeClauses) || "the whole area"}</div>
                    </div>
                  ))}
                  {coverageOnly.length > 0 && (
                    <div style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "8px 10px", background: "var(--c-panel)" }}>
                      <div style={{ ...text.itemTitle, marginBottom: 2 }}>Not yet covered by a recent audit ({coverageOnly.length})</div>
                      <div style={{ ...text.hint, marginBottom: 4 }}>No completed audit in the last 36 months reached these areas; nothing else in your records points to them.</div>
                      {coverageOnly.map((p) => (
                        <div key={p.key} style={{ padding: "6px 0", borderTop: "1px solid var(--c-border)" }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                            <span style={text.body}>{p.title}</span>
                            <span style={text.hint}>suggested {fmtDateOnly(p.plannedDate)} · {clausesByStandard(p.scopeClauses) || "the whole area"}</span>
                            {actions(p)}
                          </div>
                          {dismissBox(p)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {dismissed.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <button type="button" style={secondaryBtn} onClick={() => setShowDismissed(!showDismissed)}>{showDismissed ? "Hide" : "Show"} {dismissed.length} set aside</button>
              {showDismissed && dismissed.map((p) => {
                const d = data.dismissals.find((x) => x.proposalKey === p.key);
                return (
                  <div key={p.key} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "6px 0", borderBottom: "1px solid var(--c-border)", flexWrap: "wrap" }}>
                    <span style={{ ...text.body, color: theme.color.muted }}>{p.title}</span>
                    {d?.reason && <span style={text.hint}>{d.reason}</span>}
                    {canAudit && d && <button type="button" style={{ ...secondaryBtn, marginLeft: "auto" }} onClick={() => void restore(d.id)}>Restore</button>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </SheetCard>
  );
}
